import { apiFetch } from "@/lib/api";

/**
 * Kiểu dữ liệu và lối gọi API cho ba việc làm trong kho trên máy cầm tay:
 * cất hàng vào ô, lấy hàng cho phiếu xuất, và luân chuyển giữa các ô.
 *
 * Mọi việc đều đi qua một ô chứa cụ thể, nên `scanBin` là điểm bắt đầu chung.
 */

export type OpsWarehouse = {
  id: string;
  code: string;
  name: string;
  fullName: string;
  address: string;
  /** Số dòng còn phải cất lên kệ. */
  putawayLines: number;
  /** Số phiếu xuất đã duyệt đang chờ lấy hàng. */
  pickNotes: number;
};

export type BinContent = {
  id: string;
  productId: string;
  productCode: string;
  productName: string;
  lotNumber: string;
  qty: number;
  reservedQty: number;
  unit: string;
  expiryDate: string;
  stockBucket: string;
};

/** Một ô trong danh sách chọn — không kèm nội dung, nội dung lấy khi chọn xong. */
export type BinOption = {
  id: string;
  code: string;
  name: string;
  status: string;
  blocked: boolean;
  zoneCode: string;
  zoneName: string;
};

export type ScannedBin = {
  id: string;
  code: string;
  name: string;
  status: string;
  /** Ô đang khóa / bảo trì: không cất vào cũng không lấy ra. */
  blocked: boolean;
  zoneCode: string;
  zoneName: string;
  contents: BinContent[];
};

export type ScannedProduct = {
  productId: string;
  code: string;
  name: string;
  unit: string;
};

export type PickItem = {
  id: number;
  lineNo: number;
  productCode: string;
  productName: string;
  lotNumber: string;
  expiryDate: string;
  binId: string;
  binCode: string;
  requestedQty: number;
  pickedQty: number;
  unit: string;
  status: string;
};

export type PickTask = {
  id: string;
  code: string;
  issueType: string;
  typeLabel: string;
  /** Loại phiếu này máy cầm tay xuất được hay phải làm trên máy tính. */
  supported: boolean;
  issueDate: string;
  partner: string;
  warehouseId: string;
  warehouseName: string;
  totalItems: number;
  pickedItems: number;
  items: PickItem[];
};

export type PutawayLine = {
  taskId: string;
  taskCode: string;
  itemId: number;
  productCode: string;
  productName: string;
  batchCode: string;
  expDate: string;
  qtyToPutaway: number;
  unit: string;
  status: string;
  /** Ô đã hẹn từ đơn mua, nếu có. */
  reservedBinCode: string;
  reservedZoneName: string;
  supplierName: string;
};

type PutawayTaskResponse = {
  id: string;
  grnNumber: string;
  poCode: string;
  supplierName: string;
  status: string;
  items: {
    id: number;
    productCode: string;
    productName: string;
    batchCode: string;
    expDate: string;
    qtyToPutaway: number;
    unit: string;
    status: string;
    reservedBinCode: string;
    reservedZoneName: string;
  }[];
};

/** Kho công nhân đang đứng — nhớ lại giữa các lần mở ứng dụng. */
export type SelectedWarehouse = { id: string; code: string; name: string };

const WAREHOUSE_KEY = "mvp_ops_warehouse";

export function readSelectedWarehouse(): SelectedWarehouse | null {
  try {
    const raw = localStorage.getItem(WAREHOUSE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<SelectedWarehouse>;

    return parsed?.id ? { id: parsed.id, code: parsed.code ?? "", name: parsed.name ?? "" } : null;
  } catch {
    // Máy khóa bộ nhớ hoặc dữ liệu hỏng: coi như chưa chọn kho.
    return null;
  }
}

export function writeSelectedWarehouse(w: SelectedWarehouse | null): void {
  try {
    if (w) localStorage.setItem(WAREHOUSE_KEY, JSON.stringify(w));
    else localStorage.removeItem(WAREHOUSE_KEY);
  } catch {
    // Không ghi được thì chỉ mất phần nhớ kho, không chặn thao tác.
  }
}

/** Lấy câu báo lỗi do máy chủ gửi, không có thì dùng câu thay thế. */
export function errorText(e: unknown, fallback: string): string {
  return (e as { message?: string })?.message || fallback;
}

/** Số gọn: bỏ các số 0 thừa sau dấu thập phân. */
export function qtyText(v: number): string {
  return Number(v.toFixed(3)).toLocaleString("vi-VN");
}

export const opsApi = {
  warehouses: (token?: string) =>
    apiFetch<{ data: OpsWarehouse[] }>("/warehouse-ops/warehouses", {}, token),

  bins: (warehouseId: string, token?: string) =>
    apiFetch<{ data: BinOption[] }>(
      `/warehouse-ops/bins?warehouseId=${encodeURIComponent(warehouseId)}`,
      {},
      token,
    ),

  scanBin: (warehouseId: string, code: string, token?: string) =>
    apiFetch<{ data: ScannedBin }>(
      `/warehouse-ops/bin?warehouseId=${encodeURIComponent(warehouseId)}&code=${encodeURIComponent(code)}`,
      {},
      token,
    ),

  pickTasks: (warehouseId: string, token?: string) =>
    apiFetch<{ data: PickTask[] }>(
      `/warehouse-ops/pick-tasks?warehouseId=${encodeURIComponent(warehouseId)}`,
      {},
      token,
    ),

  scanProduct: (code: string, token?: string) =>
    apiFetch<{ data: ScannedProduct }>(
      `/warehouse-ops/product?code=${encodeURIComponent(code)}`,
      {},
      token,
    ),

  pickItem: (
    noteId: string,
    itemId: number,
    scannedBinCode: string,
    scannedProductCode: string,
    token?: string,
  ) =>
    apiFetch<{ message: string; warning: string | null; data: PickTask }>(
      `/warehouse-ops/pick-tasks/${noteId}/items/${itemId}/pick`,
      { method: "PATCH", body: JSON.stringify({ scannedBinCode, scannedProductCode }) },
      token,
    ),

  issuePickTask: (noteId: string, token?: string) =>
    apiFetch<{ message: string }>(
      `/warehouse-ops/pick-tasks/${noteId}/issue`,
      { method: "POST" },
      token,
    ),

  /**
   * Các dòng còn phải cất của cả kho, gộp từ mọi lệnh cất hàng.
   *
   * Người cầm máy đứng trước một ô và hỏi "cất gì vào đây", chứ không nghĩ theo
   * lệnh nào — nên danh sách phải phẳng. Lấy cả lệnh `in_progress`: lệnh đang
   * dở vẫn còn dòng chưa cất.
   */
  putawayLines: async (warehouseId: string, token?: string): Promise<PutawayLine[]> => {
    const res = await apiFetch<{ data: PutawayTaskResponse[] }>(
      `/putaway-tasks?status=all&per_page=200&warehouseId=${encodeURIComponent(warehouseId)}`,
      {},
      token,
    );

    return (res.data ?? [])
      .filter((t) => t.status === "pending" || t.status === "in_progress")
      .flatMap((t) =>
        (t.items ?? [])
          .filter((i) => i.status !== "completed" && i.status !== "skipped")
          .map<PutawayLine>((i) => ({
            taskId: t.id,
            taskCode: t.grnNumber || t.poCode || t.id,
            itemId: i.id,
            productCode: i.productCode,
            productName: i.productName,
            batchCode: i.batchCode ?? "",
            expDate: i.expDate ?? "",
            qtyToPutaway: i.qtyToPutaway,
            unit: i.unit ?? "",
            status: i.status,
            reservedBinCode: i.reservedBinCode ?? "",
            reservedZoneName: i.reservedZoneName ?? "",
            supplierName: t.supplierName ?? "",
          })),
      );
  },

  /** `scannedItemBarcode` để trống nghĩa là dòng được chọn tay, không qua quét. */
  confirmPutaway: (
    taskId: string,
    itemId: number,
    binId: string,
    scannedLocationBarcode: string,
    scannedItemBarcode: string,
    token?: string,
  ) =>
    apiFetch<{ message?: string }>(
      `/putaway-tasks/${taskId}/items/${itemId}/confirm`,
      {
        method: "PATCH",
        body: JSON.stringify({
          binId,
          scannedLocationBarcode,
          scannedItemBarcode: scannedItemBarcode || undefined,
        }),
      },
      token,
    ),

  relocate: (
    payload: {
      warehouseId: string;
      destWarehouseId?: string;
      moves: { binContentId: string; destBinId: string; qty: number }[];
    },
    token?: string,
  ) =>
    apiFetch<{ message?: string }>(
      "/transfer-notes/relocate",
      { method: "POST", body: JSON.stringify(payload) },
      token,
    ),
};
